/**
 * アレルゲンの記録（4 状態・品目リストの版・2026-10-07）。
 *
 * ⚠️ **記録の道具まで** — kalori はアレルゲンを推定・判定しない（ROADMAP §課金の対象）。
 * 「含まない」は御社が確かめたときだけ選ぶ（記載が無いことは「含まない」の根拠にならない）。既定は「未確認」。
 * 自社の食材・部品に付けた状態は、「材料から計算」のあと「候補」として出す。反映は御社が押す（勝手に書き換えない）。
 * 品目リストの版 = `@kalori/business/brands` の ALLERGEN_LIST_VERSION（法改正で変わる）。
 */
import { useState } from "react";
import { ALLERGEN_MANDATORY, ALLERGEN_RECOMMENDED, ALLERGEN_STATES, type AllergenState } from "@kalori/business/brands";

const STATE_KEYS = Object.keys(ALLERGEN_STATES) as AllergenState[];

export function AllergenEditor({
  items,
  onChange,
  editable,
  candidates,
}: {
  /** 品目 → 状態（無い品目は「未確認」） */
  items: Record<string, AllergenState>;
  onChange: (next: Record<string, AllergenState>) => void;
  editable: boolean;
  /** 自社の食材・部品から伝わった候補 */
  candidates: Record<string, "contains" | "may_contain">;
}) {
  const [showRecommended, setShowRecommended] = useState(
    ALLERGEN_RECOMMENDED.some((a) => items[a] && items[a] !== "unknown"),
  );
  const [extra, setExtra] = useState("");
  const listed = new Set<string>([...ALLERGEN_MANDATORY, ...ALLERGEN_RECOMMENDED]);
  const others = Object.keys(items).filter((k) => !listed.has(k));
  const pending = Object.entries(candidates).filter(([k, s]) => items[k] !== s && !(s === "may_contain" && items[k] === "contains"));

  const set = (name: string, state: AllergenState) => {
    const next = { ...items };
    if (state === "unknown" && listed.has(name)) delete next[name];
    else next[name] = state;
    onChange(next);
  };

  const row = (name: string, removable = false) => (
    <tr key={name}>
      <th>{name}</th>
      <td>
        <select
          value={items[name] ?? "unknown"}
          disabled={!editable}
          aria-label={`${name} の状態`}
          className={`allergen-state state-${items[name] ?? "unknown"}`}
          onChange={(e) => set(name, e.target.value as AllergenState)}
        >
          {STATE_KEYS.map((k) => (
            <option key={k} value={k}>
              {ALLERGEN_STATES[k]}
            </option>
          ))}
        </select>
        {removable && editable && (
          <button
            type="button"
            className="btn-link small"
            onClick={() => {
              const next = { ...items };
              delete next[name];
              onChange(next);
            }}
          >
            外す
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="card">
      <h2>アレルゲン</h2>
      <p className="muted small">
        御社で確認できた状態を記録してください。kalori はアレルゲンを推定しません。「含まない」は確かめたときだけ選び、分からなければ「未確認」のままにしてください。
      </p>
      {pending.length > 0 && editable && (
        <div className="notice small">
          <strong>材料（自社の食材・部品）から:</strong> {pending.map(([k, s]) => `${k}（${ALLERGEN_STATES[s]}）`).join("・")}
          <div>
            <button
              type="button"
              className="btn small-btn"
              onClick={() => {
                const next = { ...items };
                for (const [k, s] of pending) if (!(s === "may_contain" && next[k] === "contains")) next[k] = s;
                onChange(next);
              }}
            >
              候補を反映する
            </button>{" "}
            <span className="muted">反映したら、ほかの材料も含めて確かめてください。</span>
          </div>
        </div>
      )}
      <table className="table kv allergen-table">
        <caption className="small">特定原材料（表示の義務がある品目）</caption>
        <tbody>{ALLERGEN_MANDATORY.map((a) => row(a))}</tbody>
      </table>
      <p>
        <button type="button" className="btn-link small" onClick={() => setShowRecommended(!showRecommended)}>
          {showRecommended ? "特定原材料に準ずるもの（推奨）を閉じる" : "特定原材料に準ずるもの（推奨の 20 品目）も記録する"}
        </button>
      </p>
      {showRecommended && (
        <table className="table kv allergen-table">
          <caption className="small">特定原材料に準ずるもの（表示を推奨する品目）</caption>
          <tbody>{ALLERGEN_RECOMMENDED.map((a) => row(a))}</tbody>
        </table>
      )}
      {others.length > 0 && (
        <table className="table kv allergen-table">
          <caption className="small">そのほかの品目</caption>
          <tbody>{others.map((a) => row(a, true))}</tbody>
        </table>
      )}
      {editable && (
        <div className="search">
          <input value={extra} maxLength={40} onChange={(e) => setExtra(e.target.value)} placeholder="リストに無い品目を足す（例: 鶏卵の卵白）" aria-label="品目を足す" />
          <button
            type="button"
            className="btn"
            disabled={!extra.trim()}
            onClick={() => {
              set(extra.trim(), "contains");
              setExtra("");
            }}
          >
            足す
          </button>
        </div>
      )}
      <p className="muted small">
        承認された「含む」の品目は、御社の公開ページ（kalori.jp/r/…）と印刷用の栄養成分表に載ります。kalori の商品ページやアプリにはまだ表示していません。
      </p>
    </div>
  );
}
